import { Box, CircularProgress } from '@mui/material';
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import invariant from 'tiny-invariant';
import { TimelineGroupedByDate, addTimelineItemToGroupedTimeline } from '../utils';
import NewMessagesPill from './NewMessagesPill';
import { useChatsContext } from '../ChatsContext';
import { ChatActorType, ChatMessageType } from '../types';
import MessagesBoxView from './MessagesBoxView';

export type MessagesBoxProps = {
  setIsMessagesEndInView: (isInView: boolean) => void;
  participantsMap: Map<number, ChatActorType>;
};

export interface MessagesBoxRef {
  scrollToBottom: () => void;
}

const MessagesBox = forwardRef<MessagesBoxRef, MessagesBoxProps>(
  ({ setIsMessagesEndInView, participantsMap }, ref) => {
    const [isFetchingMore, setIsFetchingMore] = useState(false);
    const [isNewMessagesPillVisible, setIsNewMessagesPillVisible] = useState(false);
    const {
      activeChatId,
      chatTimelineNextCursor,
      fetchMoreChatTimeline,
      onNewMessage,
      offNewMessage,
      chatTimelineItems,
      viewerActor,
    } = useChatsContext();

    invariant(activeChatId, 'MessagesBox should only render with an active chat');
    invariant(chatTimelineItems, 'MessagesBox should only render with loaded timeline items');

    const containerRef = useRef<HTMLInputElement>(null);
    const loaderRef = useRef<HTMLInputElement>(null);
    const lastMessageRef = useRef<HTMLSpanElement | null>(null);
    const isLastMessageVisibleRef = useRef(false);
    const messagesRef = useRef<(HTMLElement | null)[]>([]);
    const [unreadMessageIds, setUnreadMessageIds] = useState<Set<string>>(new Set());

    const groupedTimeline = useMemo(() => {
      const newGroupedTimeline: TimelineGroupedByDate[] = [];
      for (const item of chatTimelineItems) {
        addTimelineItemToGroupedTimeline(item, newGroupedTimeline, participantsMap);
      }
      return newGroupedTimeline;
    }, [chatTimelineItems, participantsMap]);

    useEffect(() => {
      if (!messagesRef.current) return undefined;

      const messagesObserver = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setUnreadMessageIds((prev) => {
              const newSet = new Set(prev);
              // @ts-expect-error TS(2812): Property 'dataset' does not exist on type 'HTMLEle... Remove this comment to see the full error message
              newSet.delete((entry.target as HTMLElement).dataset.id);
              return newSet;
            });
            messagesObserver.unobserve(entry.target);
          }
        },
        {
          root: null,
          rootMargin: '0px',
          threshold: 0.8,
        },
      );

      setTimeout(() => {
        for (const messageNode of messagesRef.current.filter(
          // @ts-expect-error TS(2812): Property 'dataset' does not exist on type 'HTMLEle... Remove this comment to see the full error message
          (ref) => ref && unreadMessageIds.has(ref.dataset.id),
        )) {
          messagesObserver.observe(messageNode as $TSFixMe);
        }
      }, 0);

      return () => {
        messagesObserver.disconnect();
      };
    }, [unreadMessageIds]);

    const scrollToBottom = () => {
      if (containerRef.current) {
        containerRef.current.scrollTop = containerRef.current.scrollHeight;
      }
    };

    useImperativeHandle(ref, () => ({
      scrollToBottom,
    }));

    useEffect(() => {
      const lastMessageObserver = new IntersectionObserver(
        ([entry]) => {
          setIsMessagesEndInView(entry.intersectionRatio === 1);
          isLastMessageVisibleRef.current = entry.isIntersecting;
        },
        { root: null, rootMargin: '0px', threshold: [0, 1] },
      );

      setTimeout(() => {
        if (lastMessageRef.current) {
          lastMessageObserver.observe(lastMessageRef.current);
        }
      }, 0);

      return () => {
        lastMessageObserver.disconnect();
      };
    }, [groupedTimeline, setIsMessagesEndInView]);

    useEffect(() => {
      const listener = (message: ChatMessageType) => {
        if (viewerActor.id === message.senderActorId || isLastMessageVisibleRef.current) {
          setTimeout(scrollToBottom, 0);
        } else {
          setUnreadMessageIds((prev) => {
            const newSet = new Set(prev);
            newSet.add(message.id);
            return newSet;
          });
          setIsNewMessagesPillVisible(true);
        }
      };
      onNewMessage(listener);

      return () => {
        offNewMessage(listener);
      };
    }, [onNewMessage, viewerActor, offNewMessage]);

    useEffect(() => {
      if (!chatTimelineNextCursor) {
        return undefined;
      }

      const observer = new IntersectionObserver(
        async ([entry]) => {
          if (entry.isIntersecting && chatTimelineNextCursor && !isFetchingMore) {
            // @ts-expect-error TS(2531): Object is possibly 'null'.
            const prevScrollTop = containerRef.current.scrollTop;
            // Subtracting loader height which will be 0 after load
            const prevScrollHeight =
              // @ts-expect-error TS(2531): Object is possibly 'null'.
              containerRef.current.scrollHeight - loaderRef.current.scrollHeight;
            setIsFetchingMore(true);
            try {
              await fetchMoreChatTimeline({
                variables: {
                  chatId: activeChatId,
                  cursor: chatTimelineNextCursor,
                },
              });
            } finally {
              setIsFetchingMore(false);
            }
            // Maintain the scroll position by calculating the difference in the new scroll height
            // of the box vs where we were before
            // @ts-expect-error TS(2531): Object is possibly 'null'.
            const newScrollHeight = containerRef.current.scrollHeight;
            const scrollDifference = newScrollHeight - prevScrollHeight;
            // @ts-expect-error TS(2531): Object is possibly 'null'.
            containerRef.current.scrollTop = prevScrollTop + scrollDifference;
          }
        },
        {
          root: null,
          rootMargin: '0px',
          threshold: 1.0,
        },
      );
      observer.observe(loaderRef.current as $TSFixMe);

      return () => {
        observer.disconnect();
      };
    }, [chatTimelineNextCursor, fetchMoreChatTimeline, isFetchingMore, activeChatId]);

    return (
      <Box pt={4} boxSizing="border-box" flex={1} overflow="auto" ref={containerRef}>
        {unreadMessageIds.size > 0 && isNewMessagesPillVisible && (
          <NewMessagesPill
            onClick={scrollToBottom}
            count={unreadMessageIds.size}
            onDismiss={() => setIsNewMessagesPillVisible(false)}
          />
        )}
        <Box ref={loaderRef}>
          {isFetchingMore && (
            <Box display="flex" justifyContent="center" alignItems="center" pt={2} pb={4}>
              <CircularProgress size={24} />
            </Box>
          )}
        </Box>
        <MessagesBoxView
          groupedTimeline={groupedTimeline}
          setLastMessageRef={(node) => {
            lastMessageRef.current = node;
          }}
          setMessagesRef={(index, node) => {
            messagesRef.current[index] = node;
          }}
        />
      </Box>
    );
  },
);

MessagesBox.displayName = 'MessagesBox';

export default MessagesBox;
